/**
 * Pure plot fence grammar + validation + plain summary + expression DSL check.
 * Mirror: src/plots/plot-spec.ts (keep in lockstep — parity tests enforce).
 * Only allowed divergence: utf8Bytes (Buffer vs TextEncoder).
 */

/** Fence language tag (case-sensitive). */
export const PLOT_FENCE_LANG = 'plot' as const;

/** Protocol version emitted by tools; client accepts only this value in v1. */
export const PLOT_SPEC_VERSION = 1 as const;

export const PLOT_SERIES_MAX = 12;
export const PLOT_MARKS_MAX = 32;
export const PLOT_EXPR_MAX_CHARS = 200;
export const PLOT_TITLE_MAX = 80;
export const PLOT_LABEL_MAX = 80;
export const PLOT_ID_MAX = 40;
export const PLOT_PARAM_MAX = 16;
export const PLOT_FENCE_BODY_MAX_BYTES = 32 * 1024;
export const PLOT_SUMMARY_MAX_CHARS = 3500;

export const PLOT_FUNC_ALLOWLIST = [
  'sin',
  'cos',
  'tan',
  'asin',
  'acos',
  'atan',
  'exp',
  'ln',
  'log',
  'sqrt',
  'abs',
  'floor',
  'ceil',
  'min',
  'max',
  'hypot',
  'atan2',
] as const;

export const PLOT_CONST_ALLOWLIST = ['pi', 'e'] as const;

const FUNC_SET = new Set<string>(PLOT_FUNC_ALLOWLIST);
const CONST_SET = new Set<string>(PLOT_CONST_ALLOWLIST);
const COLOR_RE = /^#[0-9a-fA-F]{6}$/;
const CONTROL_CHARS = /[\x00-\x08\x0B\x0C\x0E-\x1F]/;
const ID_RE = /^[a-zA-Z][a-zA-Z0-9_]*$/;

/**
 * Render engine:
 * - function-plot: math curves (explicit / parametric / implicit) — primary teaching UI
 * - plotly: statistical scatter/line charts (optional)
 * Omitted: inferred from series types (all math → function-plot; all scatter → plotly).
 */
export type PlotEngine = 'function-plot' | 'plotly';

export type SeriesType = 'explicit' | 'parametric' | 'implicit' | 'scatter';

export interface Viewport {
  xMin: number;
  xMax: number;
  yMin: number;
  yMax: number;
  equalAspect?: boolean;
}

export interface SeriesBase {
  id?: string;
  label?: string;
  color?: string;
  strokeWidth?: number;
}

export type ScatterMode = 'lines' | 'markers' | 'lines+markers';

export type Series =
  | (SeriesBase & { type: 'explicit'; expr: string })
  | (SeriesBase & {
      type: 'parametric';
      xExpr: string;
      yExpr: string;
      tMin: number;
      tMax: number;
    })
  | (SeriesBase & { type: 'implicit'; expr: string })
  | (SeriesBase & {
      type: 'scatter';
      x: number[];
      y: number[];
      mode?: ScatterMode;
    });

export type Mark =
  | { type: 'point'; x: number; y: number; label?: string; color?: string }
  | { type: 'vline'; x: number; label?: string }
  | { type: 'hline'; y: number; label?: string }
  | { type: 'tangent'; seriesId: string; at: number; label?: string };

export type AnimationMode = 'parameter' | 'family';

export interface AnimationSpec {
  mode: AnimationMode;
  param: string;
  from: number;
  to: number;
  durationMs: number;
  autoplay?: boolean;
}

export interface PlotSpec {
  version: typeof PLOT_SPEC_VERSION;
  /** Omitted → inferred from series types (see PlotEngine). */
  engine?: PlotEngine;
  title?: string;
  viewport: Viewport;
  series: Series[];
  marks?: Mark[];
  animation?: AnimationSpec;
}

export type PlotSpecResult =
  | { ok: true; spec: PlotSpec }
  | { ok: false; error: string };

export type ExprAst =
  | { kind: 'num'; value: number }
  | { kind: 'ident'; name: string }
  | { kind: 'unary'; op: '-'; arg: ExprAst }
  | { kind: 'binary'; op: '+' | '-' | '*' | '/' | '^'; left: ExprAst; right: ExprAst }
  | { kind: 'call'; name: string; args: ExprAst[] };

export type ExprParseResult =
  | { ok: true; ast: ExprAst; freeIdents: string[] }
  | { ok: false; error: string };

function utf8Bytes(s: string): number {
  return new TextEncoder().encode(s).length;
}

function reject(error: string): PlotSpecResult {
  return { ok: false, error };
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function isFiniteNumber(n: unknown): n is number {
  return typeof n === 'number' && Number.isFinite(n);
}

// ── Expression DSL ──────────────────────────────────────────────────────────

type Tok =
  | { kind: 'num'; value: number }
  | { kind: 'ident'; name: string }
  | { kind: 'op'; op: string }
  | { kind: 'lparen' }
  | { kind: 'rparen' }
  | { kind: 'comma' };

function tokenizeExpr(src: string): { ok: true; tokens: Tok[] } | { ok: false; error: string } {
  const tokens: Tok[] = [];
  let i = 0;
  while (i < src.length) {
    const c = src[i]!;
    if (c === ' ' || c === '\t' || c === '\n' || c === '\r') {
      i += 1;
      continue;
    }
    if (c >= '0' && c <= '9') {
      let j = i;
      while (j < src.length && ((src[j]! >= '0' && src[j]! <= '9') || src[j] === '.')) j += 1;
      const raw = src.slice(i, j);
      if (!/^\d+(\.\d+)?$/.test(raw)) {
        return { ok: false, error: `invalid number in expression: ${raw}` };
      }
      const value = Number(raw);
      if (!Number.isFinite(value)) {
        return { ok: false, error: `invalid number in expression: ${raw}` };
      }
      tokens.push({ kind: 'num', value });
      i = j;
      continue;
    }
    if ((c >= 'a' && c <= 'z') || (c >= 'A' && c <= 'Z') || c === '_') {
      let j = i + 1;
      while (
        j < src.length &&
        ((src[j]! >= 'a' && src[j]! <= 'z') ||
          (src[j]! >= 'A' && src[j]! <= 'Z') ||
          (src[j]! >= '0' && src[j]! <= '9') ||
          src[j] === '_')
      ) {
        j += 1;
      }
      tokens.push({ kind: 'ident', name: src.slice(i, j) });
      i = j;
      continue;
    }
    if (c === '+' || c === '-' || c === '*' || c === '/' || c === '^') {
      tokens.push({ kind: 'op', op: c });
      i += 1;
      continue;
    }
    if (c === '(') {
      tokens.push({ kind: 'lparen' });
      i += 1;
      continue;
    }
    if (c === ')') {
      tokens.push({ kind: 'rparen' });
      i += 1;
      continue;
    }
    if (c === ',') {
      tokens.push({ kind: 'comma' });
      i += 1;
      continue;
    }
    return { ok: false, error: `invalid character in expression: ${JSON.stringify(c)}` };
  }
  return { ok: true, tokens };
}

/**
 * Parse a restricted math expression into an AST and free identifier set.
 * Free idents exclude function names and constants (pi, e).
 */
export function parseExpr(src: unknown): ExprParseResult {
  if (typeof src !== 'string') return { ok: false, error: 'expression must be a string' };
  if (CONTROL_CHARS.test(src)) {
    return { ok: false, error: 'expression contains control characters' };
  }
  const trimmed = src.trim();
  if (!trimmed) return { ok: false, error: 'expression is empty' };
  if (trimmed.length > PLOT_EXPR_MAX_CHARS) {
    return { ok: false, error: `expression exceeds ${PLOT_EXPR_MAX_CHARS} characters` };
  }
  const tok = tokenizeExpr(trimmed);
  if (!tok.ok) return tok;

  let pos = 0;
  const tokens = tok.tokens;
  const free = new Set<string>();

  function peek(): Tok | undefined {
    return tokens[pos];
  }
  function consume(): Tok | undefined {
    return tokens[pos++];
  }

  function parsePrimary(): ExprAst | { error: string } {
    const t = peek();
    if (!t) return { error: 'unexpected end of expression' };
    if (t.kind === 'num') {
      consume();
      return { kind: 'num', value: t.value };
    }
    if (t.kind === 'ident') {
      consume();
      if (peek()?.kind === 'lparen') {
        if (!FUNC_SET.has(t.name)) {
          return { error: `unknown function: ${t.name}` };
        }
        consume(); // (
        const args: ExprAst[] = [];
        if (peek()?.kind !== 'rparen') {
          for (;;) {
            const a = parseExprPrec(0);
            if ('error' in a) return a;
            args.push(a);
            if (peek()?.kind === 'comma') {
              consume();
              continue;
            }
            break;
          }
        }
        if (peek()?.kind !== 'rparen') return { error: 'expected ) after function args' };
        consume();
        const arityOk =
          (t.name === 'min' || t.name === 'max' || t.name === 'hypot' || t.name === 'atan2'
            ? args.length === 2
            : args.length === 1) ||
          (t.name === 'log' && (args.length === 1 || args.length === 2));
        // log(x) or log(x, base); min/max/hypot/atan2 need 2; rest need 1
        if (t.name === 'min' || t.name === 'max' || t.name === 'hypot' || t.name === 'atan2') {
          if (args.length !== 2) {
            return { error: `${t.name} requires exactly 2 arguments` };
          }
        } else if (t.name === 'log') {
          if (args.length !== 1 && args.length !== 2) {
            return { error: 'log requires 1 or 2 arguments' };
          }
        } else if (args.length !== 1) {
          return { error: `${t.name} requires exactly 1 argument` };
        }
        void arityOk;
        return { kind: 'call', name: t.name, args };
      }
      if (CONST_SET.has(t.name)) {
        return { kind: 'ident', name: t.name };
      }
      free.add(t.name);
      return { kind: 'ident', name: t.name };
    }
    if (t.kind === 'lparen') {
      consume();
      const inner = parseExprPrec(0);
      if ('error' in inner) return inner;
      if (peek()?.kind !== 'rparen') return { error: 'expected )' };
      consume();
      return inner;
    }
    if (t.kind === 'op' && t.op === '-') {
      consume();
      const arg = parsePrimary();
      if ('error' in arg) return arg;
      return { kind: 'unary', op: '-', arg };
    }
    return { error: 'expected number, identifier, or (' };
  }

  function precedence(op: string): number {
    if (op === '+' || op === '-') return 1;
    if (op === '*' || op === '/') return 2;
    if (op === '^') return 3;
    return -1;
  }

  function parseExprPrec(minPrec: number): ExprAst | { error: string } {
    let left = parsePrimary();
    if ('error' in left) return left;
    for (;;) {
      const t = peek();
      if (!t || t.kind !== 'op') break;
      const prec = precedence(t.op);
      if (prec < minPrec) break;
      const op = t.op as '+' | '-' | '*' | '/' | '^';
      consume();
      // right-assoc for ^
      const nextMin = op === '^' ? prec : prec + 1;
      const right = parseExprPrec(nextMin);
      if ('error' in right) return right;
      left = { kind: 'binary', op, left, right };
    }
    return left;
  }

  const ast = parseExprPrec(0);
  if ('error' in ast) return { ok: false, error: ast.error };
  if (pos !== tokens.length) {
    return { ok: false, error: 'unexpected trailing tokens in expression' };
  }
  return { ok: true, ast, freeIdents: [...free].sort() };
}

/**
 * Validate expression syntax and that free identifiers ⊆ allowed.
 */
export function validateExpr(
  src: unknown,
  allowedIdents: ReadonlySet<string>,
  field: string,
): { ok: true; ast: ExprAst } | { ok: false; error: string } {
  const parsed = parseExpr(src);
  if (!parsed.ok) return { ok: false, error: `${field}: ${parsed.error}` };
  for (const id of parsed.freeIdents) {
    if (!allowedIdents.has(id)) {
      return {
        ok: false,
        error: `${field}: unknown identifier "${id}" (allowed: ${[...allowedIdents].sort().join(', ') || 'none'})`,
      };
    }
  }
  return { ok: true, ast: parsed.ast };
}

/** Evaluate a validated AST with a numeric scope. Returns NaN on domain errors. */
export function evalExpr(ast: ExprAst, scope: Record<string, number>): number {
  switch (ast.kind) {
    case 'num':
      return ast.value;
    case 'ident': {
      if (ast.name === 'pi') return Math.PI;
      if (ast.name === 'e') return Math.E;
      const v = scope[ast.name];
      if (typeof v !== 'number' || !Number.isFinite(v)) return NaN;
      return v;
    }
    case 'unary': {
      const a = evalExpr(ast.arg, scope);
      return -a;
    }
    case 'binary': {
      const l = evalExpr(ast.left, scope);
      const r = evalExpr(ast.right, scope);
      switch (ast.op) {
        case '+':
          return l + r;
        case '-':
          return l - r;
        case '*':
          return l * r;
        case '/':
          return r === 0 ? NaN : l / r;
        case '^':
          return Math.pow(l, r);
        default:
          return NaN;
      }
    }
    case 'call': {
      const args = ast.args.map((a) => evalExpr(a, scope));
      if (args.some((a) => !Number.isFinite(a))) return NaN;
      switch (ast.name) {
        case 'sin':
          return Math.sin(args[0]!);
        case 'cos':
          return Math.cos(args[0]!);
        case 'tan':
          return Math.tan(args[0]!);
        case 'asin':
          return Math.asin(args[0]!);
        case 'acos':
          return Math.acos(args[0]!);
        case 'atan':
          return Math.atan(args[0]!);
        case 'exp':
          return Math.exp(args[0]!);
        case 'ln':
          return args[0]! <= 0 ? NaN : Math.log(args[0]!);
        case 'log':
          if (args.length === 1) {
            return args[0]! <= 0 ? NaN : Math.log10(args[0]!);
          }
          if (args[0]! <= 0 || args[1]! <= 0 || args[1] === 1) return NaN;
          return Math.log(args[0]!) / Math.log(args[1]!);
        case 'sqrt':
          return args[0]! < 0 ? NaN : Math.sqrt(args[0]!);
        case 'abs':
          return Math.abs(args[0]!);
        case 'floor':
          return Math.floor(args[0]!);
        case 'ceil':
          return Math.ceil(args[0]!);
        case 'min':
          return Math.min(args[0]!, args[1]!);
        case 'max':
          return Math.max(args[0]!, args[1]!);
        case 'hypot':
          return Math.hypot(args[0]!, args[1]!);
        case 'atan2':
          return Math.atan2(args[0]!, args[1]!);
        default:
          return NaN;
      }
    }
    default:
      return NaN;
  }
}

// ── Spec validation ─────────────────────────────────────────────────────────

function validateOptionalString(
  raw: unknown,
  field: string,
  max: number,
): { ok: true; value: string } | { ok: false; error: string } {
  if (typeof raw !== 'string') return { ok: false, error: `${field} must be a string` };
  const value = raw.trim();
  if (!value) return { ok: false, error: `${field} is empty` };
  if (value.length > max) return { ok: false, error: `${field} exceeds ${max} characters` };
  if (CONTROL_CHARS.test(value)) {
    return { ok: false, error: `${field} contains control characters` };
  }
  return { ok: true, value };
}

function validateColor(
  raw: unknown,
  field: string,
): { ok: true; value: string } | { ok: false; error: string } {
  if (typeof raw !== 'string') return { ok: false, error: `${field} must be a string` };
  if (!COLOR_RE.test(raw)) {
    return { ok: false, error: `${field} must be #RRGGBB` };
  }
  return { ok: true, value: raw.toLowerCase() };
}

function validateViewport(raw: unknown): { ok: true; value: Viewport } | { ok: false; error: string } {
  if (!isPlainObject(raw)) return { ok: false, error: 'viewport must be an object' };
  const allowed = new Set(['xMin', 'xMax', 'yMin', 'yMax', 'equalAspect']);
  for (const k of Object.keys(raw)) {
    if (!allowed.has(k)) return { ok: false, error: `unknown viewport field: ${k}` };
  }
  for (const k of ['xMin', 'xMax', 'yMin', 'yMax'] as const) {
    if (raw[k] === undefined) return { ok: false, error: `viewport.${k} is required` };
    if (!isFiniteNumber(raw[k])) {
      return { ok: false, error: `viewport.${k} must be a finite number` };
    }
  }
  const xMin = raw.xMin as number;
  const xMax = raw.xMax as number;
  const yMin = raw.yMin as number;
  const yMax = raw.yMax as number;
  if (!(xMin < xMax)) return { ok: false, error: 'viewport.xMin must be < xMax' };
  if (!(yMin < yMax)) return { ok: false, error: 'viewport.yMin must be < yMax' };
  const vp: Viewport = { xMin, xMax, yMin, yMax };
  if (raw.equalAspect !== undefined) {
    if (typeof raw.equalAspect !== 'boolean') {
      return { ok: false, error: 'viewport.equalAspect must be a boolean' };
    }
    vp.equalAspect = raw.equalAspect;
  }
  return { ok: true, value: vp };
}

function validateSeriesBase(
  raw: Record<string, unknown>,
  path: string,
  knownKeys: Set<string>,
): { ok: true; base: SeriesBase } | { ok: false; error: string } {
  for (const k of Object.keys(raw)) {
    if (!knownKeys.has(k)) return { ok: false, error: `${path}: unknown field ${k}` };
  }
  const base: SeriesBase = {};
  if (raw.id !== undefined) {
    const id = validateOptionalString(raw.id, `${path}.id`, PLOT_ID_MAX);
    if (!id.ok) return id;
    if (!ID_RE.test(id.value)) {
      return { ok: false, error: `${path}.id must match [a-zA-Z][a-zA-Z0-9_]*` };
    }
    base.id = id.value;
  }
  if (raw.label !== undefined) {
    const label = validateOptionalString(raw.label, `${path}.label`, PLOT_LABEL_MAX);
    if (!label.ok) return label;
    base.label = label.value;
  }
  if (raw.color !== undefined) {
    const color = validateColor(raw.color, `${path}.color`);
    if (!color.ok) return color;
    base.color = color.value;
  }
  if (raw.strokeWidth !== undefined) {
    if (typeof raw.strokeWidth !== 'number' || !Number.isInteger(raw.strokeWidth)) {
      return { ok: false, error: `${path}.strokeWidth must be an integer` };
    }
    if (raw.strokeWidth < 1 || raw.strokeWidth > 4) {
      return { ok: false, error: `${path}.strokeWidth must be in [1, 4]` };
    }
    base.strokeWidth = raw.strokeWidth;
  }
  return { ok: true, base };
}

const SCATTER_POINT_MAX = 5000;

function validateNumberArray(
  raw: unknown,
  field: string,
): { ok: true; value: number[] } | { ok: false; error: string } {
  if (!Array.isArray(raw)) return { ok: false, error: `${field} must be an array` };
  if (raw.length < 1) return { ok: false, error: `${field} must be non-empty` };
  if (raw.length > SCATTER_POINT_MAX) {
    return { ok: false, error: `${field} exceeds max ${SCATTER_POINT_MAX} points` };
  }
  const value: number[] = [];
  for (let i = 0; i < raw.length; i++) {
    if (!isFiniteNumber(raw[i])) {
      return { ok: false, error: `${field}[${i}] must be a finite number` };
    }
    value.push(raw[i] as number);
  }
  return { ok: true, value };
}

function validateSeries(
  raw: unknown,
  index: number,
  animParam: string | undefined,
): { ok: true; series: Series } | { ok: false; error: string } {
  const path = `series[${index}]`;
  if (!isPlainObject(raw)) return { ok: false, error: `${path} must be an object` };
  if (
    raw.type !== 'explicit' &&
    raw.type !== 'parametric' &&
    raw.type !== 'implicit' &&
    raw.type !== 'scatter'
  ) {
    return {
      ok: false,
      error: `${path}.type must be explicit|parametric|implicit|scatter`,
    };
  }

  if (raw.type === 'scatter') {
    const known = new Set(['type', 'x', 'y', 'mode', 'id', 'label', 'color', 'strokeWidth']);
    const baseR = validateSeriesBase(raw, path, known);
    if (!baseR.ok) return baseR;
    const xs = validateNumberArray(raw.x, `${path}.x`);
    if (!xs.ok) return xs;
    const ys = validateNumberArray(raw.y, `${path}.y`);
    if (!ys.ok) return ys;
    if (xs.value.length !== ys.value.length) {
      return { ok: false, error: `${path}: x and y must have the same length` };
    }
    const series: Series = {
      type: 'scatter',
      x: xs.value,
      y: ys.value,
      ...baseR.base,
    };
    if (raw.mode !== undefined) {
      if (raw.mode !== 'lines' && raw.mode !== 'markers' && raw.mode !== 'lines+markers') {
        return {
          ok: false,
          error: `${path}.mode must be lines|markers|lines+markers`,
        };
      }
      series.mode = raw.mode;
    }
    return { ok: true, series };
  }

  if (raw.type === 'explicit') {
    const known = new Set(['type', 'expr', 'id', 'label', 'color', 'strokeWidth']);
    const baseR = validateSeriesBase(raw, path, known);
    if (!baseR.ok) return baseR;
    const allowed = new Set<string>(['x']);
    if (animParam) allowed.add(animParam);
    const expr = validateExpr(raw.expr, allowed, `${path}.expr`);
    if (!expr.ok) return expr;
    return {
      ok: true,
      series: { type: 'explicit', expr: String(raw.expr).trim(), ...baseR.base },
    };
  }

  if (raw.type === 'parametric') {
    const known = new Set([
      'type',
      'xExpr',
      'yExpr',
      'tMin',
      'tMax',
      'id',
      'label',
      'color',
      'strokeWidth',
    ]);
    const baseR = validateSeriesBase(raw, path, known);
    if (!baseR.ok) return baseR;
    if (!isFiniteNumber(raw.tMin)) {
      return { ok: false, error: `${path}.tMin must be a finite number` };
    }
    if (!isFiniteNumber(raw.tMax)) {
      return { ok: false, error: `${path}.tMax must be a finite number` };
    }
    if (!(raw.tMin < raw.tMax)) {
      return { ok: false, error: `${path}.tMin must be < tMax` };
    }
    const allowed = new Set<string>(['t']);
    if (animParam) allowed.add(animParam);
    const xExpr = validateExpr(raw.xExpr, allowed, `${path}.xExpr`);
    if (!xExpr.ok) return xExpr;
    const yExpr = validateExpr(raw.yExpr, allowed, `${path}.yExpr`);
    if (!yExpr.ok) return yExpr;
    return {
      ok: true,
      series: {
        type: 'parametric',
        xExpr: String(raw.xExpr).trim(),
        yExpr: String(raw.yExpr).trim(),
        tMin: raw.tMin,
        tMax: raw.tMax,
        ...baseR.base,
      },
    };
  }

  // implicit
  const known = new Set(['type', 'expr', 'id', 'label', 'color', 'strokeWidth']);
  const baseR = validateSeriesBase(raw, path, known);
  if (!baseR.ok) return baseR;
  const allowed = new Set<string>(['x', 'y']);
  if (animParam) allowed.add(animParam);
  const expr = validateExpr(raw.expr, allowed, `${path}.expr`);
  if (!expr.ok) return expr;
  return {
    ok: true,
    series: { type: 'implicit', expr: String(raw.expr).trim(), ...baseR.base },
  };
}

function validateMark(
  raw: unknown,
  index: number,
  seriesIds: Set<string>,
  seriesById: Map<string, Series>,
): { ok: true; mark: Mark } | { ok: false; error: string } {
  const path = `marks[${index}]`;
  if (!isPlainObject(raw)) return { ok: false, error: `${path} must be an object` };
  if (raw.type === 'point') {
    const allowed = new Set(['type', 'x', 'y', 'label', 'color']);
    for (const k of Object.keys(raw)) {
      if (!allowed.has(k)) return { ok: false, error: `${path}: unknown field ${k}` };
    }
    if (!isFiniteNumber(raw.x)) return { ok: false, error: `${path}.x must be a finite number` };
    if (!isFiniteNumber(raw.y)) return { ok: false, error: `${path}.y must be a finite number` };
    const mark: Mark = { type: 'point', x: raw.x, y: raw.y };
    if (raw.label !== undefined) {
      const label = validateOptionalString(raw.label, `${path}.label`, PLOT_LABEL_MAX);
      if (!label.ok) return label;
      mark.label = label.value;
    }
    if (raw.color !== undefined) {
      const color = validateColor(raw.color, `${path}.color`);
      if (!color.ok) return color;
      mark.color = color.value;
    }
    return { ok: true, mark };
  }
  if (raw.type === 'vline') {
    const allowed = new Set(['type', 'x', 'label']);
    for (const k of Object.keys(raw)) {
      if (!allowed.has(k)) return { ok: false, error: `${path}: unknown field ${k}` };
    }
    if (!isFiniteNumber(raw.x)) return { ok: false, error: `${path}.x must be a finite number` };
    const mark: Mark = { type: 'vline', x: raw.x };
    if (raw.label !== undefined) {
      const label = validateOptionalString(raw.label, `${path}.label`, PLOT_LABEL_MAX);
      if (!label.ok) return label;
      mark.label = label.value;
    }
    return { ok: true, mark };
  }
  if (raw.type === 'hline') {
    const allowed = new Set(['type', 'y', 'label']);
    for (const k of Object.keys(raw)) {
      if (!allowed.has(k)) return { ok: false, error: `${path}: unknown field ${k}` };
    }
    if (!isFiniteNumber(raw.y)) return { ok: false, error: `${path}.y must be a finite number` };
    const mark: Mark = { type: 'hline', y: raw.y };
    if (raw.label !== undefined) {
      const label = validateOptionalString(raw.label, `${path}.label`, PLOT_LABEL_MAX);
      if (!label.ok) return label;
      mark.label = label.value;
    }
    return { ok: true, mark };
  }
  if (raw.type === 'tangent') {
    const allowed = new Set(['type', 'seriesId', 'at', 'label']);
    for (const k of Object.keys(raw)) {
      if (!allowed.has(k)) return { ok: false, error: `${path}: unknown field ${k}` };
    }
    if (typeof raw.seriesId !== 'string' || !raw.seriesId.trim()) {
      return { ok: false, error: `${path}.seriesId is required` };
    }
    const seriesId = raw.seriesId.trim();
    if (!seriesIds.has(seriesId)) {
      return { ok: false, error: `${path}.seriesId "${seriesId}" does not match any series.id` };
    }
    const series = seriesById.get(seriesId);
    if (!series) {
      return { ok: false, error: `${path}.seriesId "${seriesId}" not found` };
    }
    if (series.type === 'implicit') {
      return { ok: false, error: `${path}: tangent is not supported for implicit series in v1` };
    }
    if (!isFiniteNumber(raw.at)) {
      return { ok: false, error: `${path}.at must be a finite number` };
    }
    const mark: Mark = { type: 'tangent', seriesId, at: raw.at };
    if (raw.label !== undefined) {
      const label = validateOptionalString(raw.label, `${path}.label`, PLOT_LABEL_MAX);
      if (!label.ok) return label;
      mark.label = label.value;
    }
    return { ok: true, mark };
  }
  return { ok: false, error: `${path}.type must be point|vline|hline|tangent` };
}

function validateAnimation(
  raw: unknown,
): { ok: true; value: AnimationSpec } | { ok: false; error: string } {
  if (!isPlainObject(raw)) return { ok: false, error: 'animation must be an object' };
  const allowed = new Set(['mode', 'param', 'from', 'to', 'durationMs', 'autoplay']);
  for (const k of Object.keys(raw)) {
    if (!allowed.has(k)) return { ok: false, error: `unknown animation field: ${k}` };
  }
  if (raw.mode !== 'parameter' && raw.mode !== 'family') {
    return { ok: false, error: 'animation.mode must be parameter|family' };
  }
  const param = validateOptionalString(raw.param, 'animation.param', PLOT_PARAM_MAX);
  if (!param.ok) return param;
  if (!ID_RE.test(param.value)) {
    return { ok: false, error: 'animation.param must match [a-zA-Z][a-zA-Z0-9_]*' };
  }
  if (param.value === 'x' || param.value === 'y' || param.value === 't') {
    return { ok: false, error: 'animation.param must not be x, y, or t' };
  }
  if (!isFiniteNumber(raw.from)) {
    return { ok: false, error: 'animation.from must be a finite number' };
  }
  if (!isFiniteNumber(raw.to)) {
    return { ok: false, error: 'animation.to must be a finite number' };
  }
  if (raw.from === raw.to) {
    return { ok: false, error: 'animation.from must differ from to' };
  }
  if (typeof raw.durationMs !== 'number' || !Number.isInteger(raw.durationMs) || raw.durationMs <= 0) {
    return { ok: false, error: 'animation.durationMs must be a positive integer' };
  }
  const anim: AnimationSpec = {
    mode: raw.mode,
    param: param.value,
    from: raw.from,
    to: raw.to,
    durationMs: raw.durationMs,
  };
  if (raw.autoplay !== undefined) {
    if (typeof raw.autoplay !== 'boolean') {
      return { ok: false, error: 'animation.autoplay must be a boolean' };
    }
    anim.autoplay = raw.autoplay;
  }
  return { ok: true, value: anim };
}

/**
 * Validate a loose object (tool params or parsed fence JSON) into PlotSpec.
 * No silent defaults for required fields.
 */
export function validatePlotSpec(input: unknown): PlotSpecResult {
  if (!isPlainObject(input)) return reject('plot spec must be an object');
  const allowed = new Set([
    'version',
    'engine',
    'title',
    'viewport',
    'series',
    'marks',
    'animation',
  ]);
  for (const k of Object.keys(input)) {
    if (!allowed.has(k)) return reject(`unknown plot field: ${k}`);
  }

  if (input.version !== PLOT_SPEC_VERSION) {
    return reject(`version must be ${PLOT_SPEC_VERSION}`);
  }
  if (input.viewport === undefined) return reject('viewport is required');
  if (input.series === undefined) return reject('series is required');

  let engine: PlotEngine | undefined;
  if (input.engine !== undefined) {
    if (input.engine !== 'function-plot' && input.engine !== 'plotly') {
      return reject('engine must be function-plot|plotly');
    }
    engine = input.engine;
  }

  let animParam: string | undefined;
  let animation: AnimationSpec | undefined;
  if (input.animation !== undefined) {
    const a = validateAnimation(input.animation);
    if (!a.ok) return a;
    animation = a.value;
    animParam = a.value.param;
  }

  const vp = validateViewport(input.viewport);
  if (!vp.ok) return vp;

  if (!Array.isArray(input.series)) return reject('series must be an array');
  if (input.series.length < 1) return reject('series must have at least 1 item');
  if (input.series.length > PLOT_SERIES_MAX) {
    return reject(`series exceeds max ${PLOT_SERIES_MAX}`);
  }

  const series: Series[] = [];
  const seriesIds = new Set<string>();
  const seriesById = new Map<string, Series>();
  for (let i = 0; i < input.series.length; i++) {
    const s = validateSeries(input.series[i], i, animParam);
    if (!s.ok) return s;
    if (s.series.id !== undefined) {
      if (seriesIds.has(s.series.id)) {
        return reject(`duplicate series.id: ${s.series.id}`);
      }
      seriesIds.add(s.series.id);
      seriesById.set(s.series.id, s.series);
    }
    series.push(s.series);
  }

  const hasMath = series.some(
    (s) => s.type === 'explicit' || s.type === 'parametric' || s.type === 'implicit',
  );
  const hasScatter = series.some((s) => s.type === 'scatter');
  if (hasMath && hasScatter) {
    return reject('cannot mix math series (explicit|parametric|implicit) with scatter in one plot');
  }
  if (engine === 'function-plot' && hasScatter) {
    return reject('engine function-plot does not support scatter series (use engine plotly)');
  }
  if (engine === 'plotly' && hasMath) {
    return reject(
      'engine plotly does not support explicit|parametric|implicit (use engine function-plot)',
    );
  }
  if (engine === undefined) {
    engine = hasScatter ? 'plotly' : 'function-plot';
  }
  if (hasScatter && engine !== 'plotly') {
    return reject('scatter series require engine plotly');
  }
  if (animation !== undefined && engine === 'plotly') {
    return reject('animation is only supported with engine function-plot');
  }

  let marks: Mark[] | undefined;
  if (input.marks !== undefined) {
    if (engine === 'plotly') {
      return reject('marks are only supported with engine function-plot');
    }
    if (!Array.isArray(input.marks)) return reject('marks must be an array');
    if (input.marks.length > PLOT_MARKS_MAX) {
      return reject(`marks exceeds max ${PLOT_MARKS_MAX}`);
    }
    marks = [];
    for (let i = 0; i < input.marks.length; i++) {
      const m = validateMark(input.marks[i], i, seriesIds, seriesById);
      if (!m.ok) return m;
      marks.push(m.mark);
    }
  }

  const spec: PlotSpec = {
    version: PLOT_SPEC_VERSION,
    engine,
    viewport: vp.value,
    series,
  };
  if (input.title !== undefined) {
    const title = validateOptionalString(input.title, 'title', PLOT_TITLE_MAX);
    if (!title.ok) return title;
    spec.title = title.value;
  }
  if (marks !== undefined) spec.marks = marks;
  if (animation !== undefined) spec.animation = animation;

  const fenceBody = JSON.stringify(spec);
  if (utf8Bytes(fenceBody) > PLOT_FENCE_BODY_MAX_BYTES) {
    return reject(`plot fence body exceeds ${PLOT_FENCE_BODY_MAX_BYTES} bytes`);
  }

  return { ok: true, spec };
}

/**
 * Parse a ```plot fence body (JSON object) into PlotSpec.
 */
export function parsePlotFenceBody(body: string): PlotSpecResult {
  if (typeof body !== 'string') return reject('plot fence body must be a string');
  if (utf8Bytes(body) > PLOT_FENCE_BODY_MAX_BYTES) {
    return reject(`plot fence body exceeds ${PLOT_FENCE_BODY_MAX_BYTES} bytes`);
  }
  const trimmed = body.trim();
  if (!trimmed) return reject('plot fence body is empty');
  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return reject('plot fence body is not valid JSON');
  }
  return validatePlotSpec(parsed);
}

/** Minified single-line JSON fence body. */
export function toFence(spec: PlotSpec): string {
  return JSON.stringify(spec);
}

/** Cross-channel plain-text summary (no fence). */
export function toPlainSummary(spec: PlotSpec): string {
  const lines: string[] = [];
  if (spec.title) lines.push(`Plot: ${spec.title}`);
  else lines.push('Plot');
  const { xMin, xMax, yMin, yMax } = spec.viewport;
  lines.push(`Viewport: x∈[${xMin}, ${xMax}], y∈[${yMin}, ${yMax}]`);
  if (spec.engine) lines.push(`Engine: ${spec.engine}`);
  for (let i = 0; i < spec.series.length; i++) {
    const s = spec.series[i]!;
    const tag = s.label ?? s.id ?? `#${i + 1}`;
    if (s.type === 'explicit') {
      lines.push(`• ${tag}: y = ${s.expr}`);
    } else if (s.type === 'parametric') {
      lines.push(`• ${tag}: x=${s.xExpr}, y=${s.yExpr}, t∈[${s.tMin}, ${s.tMax}]`);
    } else if (s.type === 'implicit') {
      lines.push(`• ${tag}: ${s.expr} = 0`);
    } else {
      lines.push(`• ${tag}: scatter (${s.x.length} points)`);
    }
  }
  if (spec.marks && spec.marks.length > 0) {
    lines.push(`Marks: ${spec.marks.length}`);
  }
  if (spec.animation) {
    lines.push(
      `Animation (static in v1): ${spec.animation.mode} ${spec.animation.param}∈[${spec.animation.from}, ${spec.animation.to}]`,
    );
  }
  let out = lines.join('\n');
  if (out.length > PLOT_SUMMARY_MAX_CHARS) {
    out = out.slice(0, PLOT_SUMMARY_MAX_CHARS - 1) + '…';
  }
  return out;
}
